import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, Divider, IconButton, MenuItem, Radio,
  Stack, Step, StepLabel, Stepper, Switch, TextField, Tooltip, Typography,
  FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useGetallLocationQuery, useGetallGradeQuery } from "../../../Api/attoDeskApi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { STUDENT_ENROLLMENT_TYPES } from "../../../Core/constants/studentEnrollmentTypes";
import { STUDY_MODE_OPTIONS } from "../../Student/studentDisplay";
import { eligibilityLabel, eligibilityChipTone, sourceSegmentLabel } from "./eligibilityLabels";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate, useParams } from "react-router-dom";
import useUser from "../../../Core/hook/useUser";
import { Privileges } from "../../../Core/Enum/enum";
import { useNotifier } from "../../../Core/Notifier";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../../Course/shared/moduleSx";
import {
  AudienceFilter, CampaignChannel, CampaignClassification, CampaignPurpose,
  ICampaign, createCampaign, fetchCampaign, previewExternal, previewRecipients,
  scheduleCampaign, sendCampaign, testEmail, testSms, updateCampaign,
} from "../../../Api/campaignsApi";
import { parseCsv, parsePastedContacts, ParsedContact } from "./parseContacts";
import { smsSegments } from "../../Settings/CommunicationTemplates/previewUtils";

const STEPS = ["Campaign", "Recipients", "Content", "Review", "Send"];

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44, borderRadius: "10px", bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
} as const;

const CampaignWizardPage: React.FC = () => {
  const { id: routeId } = useParams<{ id?: string }>();
  const isEdit = Boolean(routeId);
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const { userHasRight, isSystemAdmin } = useUser();
  const canSendTransactional = isSystemAdmin || userHasRight(Privileges.CampaignSendTransactional);

  const [step, setStep]                 = useState(0);
  const [campaignId, setCampaignId]     = useState<number | null>(null);
  const [saving, setSaving]             = useState(false);

  // Step 1 — Campaign
  const [name, setName]                 = useState("");
  const [channel, setChannel]           = useState<CampaignChannel>("email");
  const [classification, setClassification] = useState<CampaignClassification>("marketing");
  const [purpose, setPurpose]           = useState<CampaignPurpose>("newsletter");

  // Step 2 — Recipients
  const [filter, setFilter]             = useState<AudienceFilter>({
    students: { enabled: false, active: true },
    parents:  { enabled: false, of_active_students: true, is_email_notification: true },
    staff:    { enabled: false, active: true },
    admins:   { enabled: false, active: true },
    externals: [],
  });
  const [pasted, setPasted]             = useState("");
  const [csvContacts, setCsvContacts]   = useState<ParsedContact[]>([]);
  const [previewStats, setPreviewStats] = useState<any | null>(null);
  const [previewSamples, setPreviewSamples] = useState<any[]>([]);
  const [previewPage, setPreviewPage]   = useState(1);
  const [previewPerPage, setPreviewPerPage] = useState<25 | 50 | 100>(25);
  const [previewLastPage, setPreviewLastPage] = useState(1);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewSearch, setPreviewSearch] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sampleOpen, setSampleOpen]     = useState(false);
  const debouncedSearch                 = useDebounce(previewSearch, 300);

  // Step 3 — Content
  const [subject, setSubject]           = useState("");
  const [preheader, setPreheader]       = useState("");
  const [body, setBody]                 = useState("");
  const [testTo, setTestTo]             = useState("");
  const [testSending, setTestSending]   = useState(false);

  // Step 5 — Send / Schedule
  const [scheduleMode, setScheduleMode] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt]   = useState<string>("");
  const [sending, setSending]           = useState(false);

  // ── Load existing campaign for edit mode ────────────────────────
  useEffect(() => {
    if (! isEdit || ! routeId) return;
    fetchCampaign(Number(routeId))
      .then(({ campaign }) => {
        setCampaignId(campaign.id);
        setName(campaign.name);
        setChannel(campaign.channel);
        setClassification(campaign.classification);
        setPurpose(campaign.purpose);
        setSubject(campaign.subject || "");
        setPreheader(campaign.preheader || "");
        setBody(campaign.body || "");
        if (campaign.audience_filter) setFilter(campaign.audience_filter);
        if (campaign.scheduled_at) {
          setScheduleMode("later");
          setScheduledAt(campaign.scheduled_at.slice(0, 16));
        }
      })
      .catch((e) => showErrorMessage(e?.message || "Failed to load campaign."));
  }, [isEdit, routeId, showErrorMessage]);

  // ── Persist draft (create-on-first-save, update-on-subsequent) ──
  const persist = useCallback(async (): Promise<ICampaign | null> => {
    setSaving(true);
    try {
      const externals = [
        ...parsePastedContacts(pasted, channel),
        ...csvContacts,
      ];
      const payload: any = {
        name: name.trim(),
        channel, classification, purpose,
        subject: channel === "email" ? subject.trim() || " " : null,
        preheader: preheader.trim() || null,
        body: body || " ",
        audience_filter: { ...filter, externals },
      };
      let saved: ICampaign;
      if (campaignId) {
        saved = await updateCampaign(campaignId, payload);
      } else {
        saved = await createCampaign(payload);
        setCampaignId(saved.id);
      }
      return saved;
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to save draft.");
      return null;
    } finally { setSaving(false); }
  }, [pasted, channel, csvContacts, name, classification, purpose, subject, preheader, body, filter, campaignId, showErrorMessage]);

  // ── Recipient preview — called on entering step 3 (Review) ──────
  // `page`/`search` are optional; when they change the modal refetches
  // just the sample slice while stats stay authoritative.
  const runPreview = useCallback(async (opts: { page?: number; per_page?: number; search?: string } = {}) => {
    const saved = await persist();
    if (! saved) return;
    setPreviewLoading(true);
    try {
      const externals = [
        ...parsePastedContacts(pasted, channel),
        ...csvContacts,
      ];
      const page     = opts.page     ?? 1;
      const per_page = opts.per_page ?? previewPerPage;
      const search   = opts.search   ?? "";
      // Two calls merged locally — existing filter preview + external preview.
      // Backend runs the same normalization/suppression against both.
      const [existing, external] = await Promise.all([
        previewRecipients(saved.id, { ...filter, externals: [] }, { page, per_page, search }),
        externals.length ? previewExternal(saved.id, externals) : Promise.resolve({ stats: emptyStats(), samples: [] }),
      ]);
      setPreviewStats(mergeStats(existing.stats, external.stats));
      setPreviewSamples([...existing.samples, ...external.samples]);
      const pag = existing.pagination;
      setPreviewPage(pag?.current_page ?? 1);
      setPreviewLastPage(pag?.last_page ?? 1);
      setPreviewTotal((pag?.total ?? 0) + external.samples.length);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to preview recipients.");
    } finally { setPreviewLoading(false); }
  }, [persist, pasted, channel, csvContacts, filter, previewPerPage, showErrorMessage]);

  // Debounced search + page-size change refetch. Only active once the
  // sample dialog is open — otherwise every keystroke would trigger a
  // network call while the user is still in the wizard's earlier steps.
  useEffect(() => {
    if (! sampleOpen) return;
    if (! campaignId)  return;
    runPreview({ page: 1, per_page: previewPerPage, search: debouncedSearch });
    // Reset the visible page to 1 whenever search/per_page change.
    setPreviewPage(1);
    // Deliberately omit runPreview from deps — its identity churns
    // with every state update; we want to re-run ONLY when the
    // debounced search or per_page changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, previewPerPage, sampleOpen]);

  // ── Actions ─────────────────────────────────────────────────────
  const canAdvanceFrom = (s: number): { ok: boolean; msg?: string } => {
    if (s === 0) {
      if (! name.trim())        return { ok: false, msg: "Campaign name is required." };
      if (classification === "transactional" && ! canSendTransactional)
        return { ok: false, msg: "You do not have permission to send transactional campaigns." };
      return { ok: true };
    }
    if (s === 2) {
      if (channel === "email" && ! subject.trim()) return { ok: false, msg: "Email subject is required." };
      if (! body.trim())                            return { ok: false, msg: "Content body is required." };
      return { ok: true };
    }
    return { ok: true };
  };

  const goNext = async () => {
    const guard = canAdvanceFrom(step);
    if (! guard.ok) { showErrorMessage(guard.msg!); return; }
    // Save + preview on entering Review.
    if (step === 2) {
      setPreviewPage(1);
      setPreviewSearch("");
      await runPreview({ page: 1, search: "" });
    } else {
      // Draft persistence at each step so a refresh doesn't lose work.
      await persist();
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const handleTest = async () => {
    if (! campaignId) { showErrorMessage("Save a draft first."); return; }
    if (! testTo.trim()) { showErrorMessage("Enter a test recipient."); return; }
    setTestSending(true);
    try {
      if (channel === "email") await testEmail(campaignId, testTo.trim());
      else                     await testSms(campaignId, testTo.trim());
      showMessage(`Test ${channel === "email" ? "email" : "SMS"} sent to ${testTo.trim()}.`);
    } catch (e: any) {
      showErrorMessage(e?.message || "Test send failed.");
    } finally { setTestSending(false); }
  };

  const handleFinalSubmit = async () => {
    if (! campaignId) return;
    if (! previewStats || previewStats.valid === 0) {
      showErrorMessage("No eligible recipients. Adjust audience or check consent status.");
      return;
    }
    setSending(true);
    try {
      if (scheduleMode === "now") {
        await sendCampaign(campaignId);
        showMessage("Campaign queued for delivery.");
      } else {
        if (! scheduledAt) { showErrorMessage("Choose a scheduled time."); return; }
        await scheduleCampaign(campaignId, new Date(scheduledAt).toISOString());
        showMessage("Campaign scheduled.");
      }
      navigate(`/communication/newsletters/${campaignId}`);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to submit campaign.");
    } finally { setSending(false); }
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      {/* Header */}
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Back to campaigns">
            <IconButton onClick={() => navigate("/communication/newsletters")}
              sx={{ width: 40, height: 40, borderRadius: "10px",
                    bgcolor: tint12(B13.primary), color: B13.primary,
                    "&:hover": { bgcolor: B13.primary, color: "#fff" } }}
            ><ArrowBackIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em" }}>
              {isEdit ? "Edit campaign" : "New campaign"}
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2 }}>
              {saving ? "Saving draft…" : "Every step autosaves as a draft."}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Stepper */}
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((s) => (
            <Step key={s}><StepLabel>{s}</StepLabel></Step>
          ))}
        </Stepper>
      </Box>

      {/* Step body */}
      {step === 0 && (
        <StepCampaign
          name={name} setName={setName}
          channel={channel} setChannel={setChannel}
          classification={classification} setClassification={setClassification}
          purpose={purpose} setPurpose={setPurpose}
          canSendTransactional={canSendTransactional}
        />
      )}
      {step === 1 && (
        <StepRecipients
          channel={channel}
          filter={filter} setFilter={setFilter}
          pasted={pasted} setPasted={setPasted}
          csvContacts={csvContacts} setCsvContacts={setCsvContacts}
        />
      )}
      {step === 2 && (
        <StepContent
          channel={channel}
          subject={subject} setSubject={setSubject}
          preheader={preheader} setPreheader={setPreheader}
          body={body} setBody={setBody}
          testTo={testTo} setTestTo={setTestTo}
          onTest={handleTest} testSending={testSending}
        />
      )}
      {step === 3 && (
        <StepReview
          name={name} channel={channel} classification={classification} purpose={purpose}
          subject={subject} body={body}
          previewStats={previewStats} previewSamples={previewSamples}
          previewLoading={previewLoading}
          onOpenSamples={() => setSampleOpen(true)}
        />
      )}
      {step === 4 && (
        <StepSend
          scheduleMode={scheduleMode} setScheduleMode={setScheduleMode}
          scheduledAt={scheduledAt} setScheduledAt={setScheduledAt}
          previewStats={previewStats}
        />
      )}

      {/* Footer */}
      <Box sx={{ mt: 2, ...surfaceSx, p: 2, borderRadius: 3,
                 display: "flex", justifyContent: "space-between" }}>
        <Button onClick={goBack} disabled={step === 0 || sending}
          sx={{ textTransform: "none" }}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={goNext} disabled={saving}
            sx={{ textTransform: "none", bgcolor: B13.primary,
                  "&:hover": { bgcolor: B13.primary }, borderRadius: "10px",
                  fontWeight: 600, px: 3 }}
          >Next</Button>
        ) : (
          <Button variant="contained" onClick={handleFinalSubmit}
            disabled={sending || ! previewStats || previewStats.valid === 0}
            startIcon={scheduleMode === "now" ? <SendOutlinedIcon /> : <ScheduleOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: B13.primary,
                  "&:hover": { bgcolor: B13.primary }, borderRadius: "10px",
                  fontWeight: 600, px: 3 }}
          >{sending ? "Submitting…" : scheduleMode === "now" ? "Send now" : "Schedule"}</Button>
        )}
      </Box>

      {/* Sample recipient dialog — server-side pagination + debounced
          search + friendly eligibility labels. Only eligible AND
          non-eligible rows are surfaced (backend F1b addition) so
          admins can see WHY someone was skipped. */}
      <Dialog open={sampleOpen} onClose={() => setSampleOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Recipient list</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }} alignItems="center">
            <TextField size="small" placeholder="Search name or address…"
              value={previewSearch}
              onChange={(e) => setPreviewSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <TextField select size="small" label="Page size"
              value={previewPerPage}
              onChange={(e) => setPreviewPerPage(Number(e.target.value) as 25 | 50 | 100)}
              sx={{ minWidth: 120 }}
            >
              {[25, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>{n} / page</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "1.4fr 100px 1.6fr 100px 140px 1.4fr",
            gap: 1, fontSize: 12.5, alignItems: "center",
          }}>
            <HeaderCell>Name</HeaderCell>
            <HeaderCell>Recipient Type</HeaderCell>
            <HeaderCell>Contact</HeaderCell>
            <HeaderCell>Source</HeaderCell>
            <HeaderCell>Eligibility</HeaderCell>
            <HeaderCell>Reason</HeaderCell>
            {previewLoading ? (
              <Box sx={{ gridColumn: "1 / -1", py: 3, textAlign: "center", color: B13Ink.ink2 }}>Loading…</Box>
            ) : previewSamples.length === 0 ? (
              <Box sx={{ gridColumn: "1 / -1", py: 3, textAlign: "center", color: B13Ink.ink2 }}>No matches.</Box>
            ) : previewSamples.map((s: any, i: number) => {
              const tone  = eligibilityChipTone(s.eligibility);
              const label = eligibilityLabel(s.eligibility);
              const isOk  = s.eligibility === "eligible" || s.eligibility === "transactional";
              return (
                <React.Fragment key={i}>
                  <Box>{s.name || "—"}</Box>
                  <Box sx={{ textTransform: "capitalize" }}>{s.contact_type}</Box>
                  <Box sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{s.address || "—"}</Box>
                  <Box sx={{ color: B13Ink.ink2 }}>{sourceSegmentLabel(s.source_segment)}</Box>
                  <Box>
                    <Chip size="small" label={isOk ? "Eligible" : "Skipped"}
                      sx={{ height: 20, fontSize: 10.5, bgcolor: tone.bg, color: tone.fg }} />
                  </Box>
                  <Box sx={{ color: isOk ? B13Ink.ink2 : "#B91C1C" }}>{label}</Box>
                </React.Fragment>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button size="small" startIcon={<ChevronLeftIcon />}
              disabled={previewPage <= 1 || previewLoading}
              onClick={async () => {
                const p = Math.max(1, previewPage - 1);
                setPreviewPage(p);
                await runPreview({ page: p, per_page: previewPerPage, search: debouncedSearch });
              }}
              sx={{ textTransform: "none" }}>Prev</Button>
            <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2, fontWeight: 700 }}>
              {rangeLabel(previewPage, previewPerPage, previewTotal)}
            </Typography>
            <Button size="small" endIcon={<ChevronRightIcon />}
              disabled={previewPage >= previewLastPage || previewLoading}
              onClick={async () => {
                const p = Math.min(previewLastPage, previewPage + 1);
                setPreviewPage(p);
                await runPreview({ page: p, per_page: previewPerPage, search: debouncedSearch });
              }}
              sx={{ textTransform: "none" }}>Next</Button>
          </Stack>
          <Button onClick={() => setSampleOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ═════════ Step components ═════════════════════════════════════════

const StepCampaign: React.FC<any> = ({
  name, setName, channel, setChannel, classification, setClassification,
  purpose, setPurpose, canSendTransactional,
}) => (
  <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
    <Stack spacing={2}>
      <TextField fullWidth required label="Campaign name" value={name}
        onChange={(e) => setName(e.target.value)} sx={filterFieldSx} />
      <Box>
        <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, mb: 1 }}>Channel</Typography>
        <Stack direction="row" spacing={1}>
          {(["email", "sms"] as CampaignChannel[]).map((c) => {
            const Icon = c === "email" ? EmailOutlinedIcon : SmsOutlinedIcon;
            const active = channel === c;
            return (
              <Box key={c} component="button" type="button" onClick={() => setChannel(c)}
                sx={{
                  flex: 1, p: 2, cursor: "pointer",
                  border: `2px solid ${active ? B13.primary : B13.line}`,
                  borderRadius: 2, bgcolor: active ? tint12(B13.primary) : "#fff",
                  color: active ? B13.primary : B13.ink,
                  display: "flex", alignItems: "center", gap: 1.5,
                }}
              >
                <Icon fontSize="medium" />
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700 }}>{c === "email" ? "Email" : "SMS"}</Typography>
                  <Typography sx={{ fontSize: 12, color: B13Ink.ink2 }}>
                    {c === "email" ? "Rich HTML message" : "160-char text message"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
      <Box>
        <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, mb: 1 }}>Classification</Typography>
        <Stack spacing={1}>
          <FormControlLabel
            control={<Radio checked={classification === "marketing"} onChange={() => setClassification("marketing")} />}
            label={<Typography>Marketing — subject to consent + unsubscribe rules</Typography>}
          />
          <FormControlLabel
            control={<Radio checked={classification === "transactional"} onChange={() => setClassification("transactional")} disabled={! canSendTransactional} />}
            label={
              <Typography sx={{ color: canSendTransactional ? "inherit" : B13Ink.ink3 }}>
                Transactional — bypasses marketing suppression
                {! canSendTransactional && " (requires Campaign_SendTransactional)"}
              </Typography>
            }
          />
        </Stack>
      </Box>
      <TextField select fullWidth label="Purpose" value={purpose}
        onChange={(e) => setPurpose(e.target.value as CampaignPurpose)}
        sx={filterFieldSx}
      >
        {["newsletter","announcement","promotion","reminder","event","general"].map((p) => (
          <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>{p}</MenuItem>
        ))}
      </TextField>
    </Stack>
  </Box>
);

// ── LookupOption shape used by every searchable Autocomplete ─────
type LookupOption = { id: number; label: string };

const StepRecipients: React.FC<any> = ({
  channel, filter, setFilter, pasted, setPasted, csvContacts, setCsvContacts,
}) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);

  // Reuse the same lookup APIs the Student / Course wizards call —
  // never duplicate lookup endpoints (Pass F1 §1).
  const { data: locationsResp } = useGetallLocationQuery(
    { searchText: "" }, { refetchOnMountOrArgChange: false },
  );
  const { data: gradesResp } = useGetallGradeQuery(
    { searchText: "" }, { refetchOnMountOrArgChange: false },
  );
  const locations: LookupOption[] = useMemo(() => {
    const list: any[] = (locationsResp as any)?.data ?? [];
    return list.map((l: any) => ({ id: l.id, label: l.location_name ?? `Location #${l.id}` }));
  }, [locationsResp]);
  const grades: LookupOption[] = useMemo(() => {
    const list: any[] = (gradesResp as any)?.data ?? [];
    // Grade rows in this codebase use several column names depending
    // on the endpoint. Fall through until we find one — never render
    // "undefined" or the numeric id alone.
    return list.map((g: any) => ({
      id: g.id,
      label: g.grade_name ?? g.grade ?? g.name ?? `Grade #${g.id}`,
    }));
  }, [gradesResp]);

  const programOptions: LookupOption[] = useMemo(() =>
    STUDENT_ENROLLMENT_TYPES.map((t, i) => ({ id: i, label: t.title, value: t.value } as any)),
  []);
  const studyModeOptions: LookupOption[] = useMemo(() =>
    STUDY_MODE_OPTIONS.map((m, i) => ({ id: i, label: m.label, value: m.value } as any)),
  []);

  const handleFile = async (f: File) => {
    setCsvError(null);
    if (f.size > 2_000_000) { setCsvError("File is larger than 2 MB — split it into batches."); return; }
    const text = await f.text();
    const parsed = parseCsv(text, channel);
    if (parsed.length === 0) { setCsvError("No valid contacts found in CSV."); return; }
    setCsvContacts(parsed);
  };

  const pastedPreview = useMemo(() => parsePastedContacts(pasted, channel), [pasted, channel]);

  // Resolve stored id/value arrays back to full option objects for
  // the Autocomplete `value` prop. Missing ids (stale filter after a
  // lookup row was deleted) are silently dropped — never crash.
  const optsFromIds = (opts: LookupOption[], ids: number[] | undefined): LookupOption[] =>
    (ids ?? []).map((id) => opts.find((o) => o.id === id)!).filter(Boolean);
  const optsFromValues = (opts: LookupOption[], values: string[] | undefined): LookupOption[] =>
    (values ?? []).map((v) => opts.find((o) => (o as any).value === v)!).filter(Boolean);

  const setSegment = <K extends keyof AudienceFilter>(k: K, patch: any) =>
    setFilter({ ...filter, [k]: { ...(filter as any)[k], ...patch } });

  return (
    <Stack spacing={2}>
      {/* ── Students ─────────────────────────────────────────────── */}
      <SegmentCard
        icon={<SchoolOutlinedIcon />}
        title="Students"
        subtitle={channel === "email"
          ? "Filter students; the message is delivered to the linked parent/guardian email."
          : "Filter students; the message is delivered to the linked parent/guardian phone."}
        enabled={!! filter.students?.enabled}
        onToggle={(v) => setSegment("students", { enabled: v })}
      >
        <Stack spacing={1.5}>
          <FormControlLabel
            control={<Switch checked={filter.students?.active ?? true}
              onChange={(_, v) => setSegment("students", { active: v })} />}
            label="Active students only (excludes dropped / inactive)"
          />
          <SearchableSelect
            label="Locations"
            options={locations}
            value={optsFromIds(locations, filter.students?.location_ids)}
            onChange={(v) => setSegment("students", { location_ids: v.map((o) => o.id) })}
            placeholder="All locations"
          />
          <SearchableSelect
            label="Grades"
            options={grades}
            value={optsFromIds(grades, filter.students?.grade_ids)}
            onChange={(v) => setSegment("students", { grade_ids: v.map((o) => o.id) })}
            placeholder="All grades"
          />
          <SearchableSelect
            label="Program / Service Type"
            options={programOptions}
            value={optsFromValues(programOptions, filter.students?.program_types)}
            onChange={(v) => setSegment("students", { program_types: v.map((o) => (o as any).value) })}
            placeholder="All program types"
          />
          <SearchableSelect
            label="Study Mode"
            options={studyModeOptions}
            value={optsFromValues(studyModeOptions, filter.students?.study_modes)}
            onChange={(v) => setSegment("students", { study_modes: v.map((o) => (o as any).value) })}
            placeholder="All study modes"
          />
        </Stack>
      </SegmentCard>

      {/* ── Parents ──────────────────────────────────────────────── */}
      <SegmentCard
        icon={<FamilyRestroomOutlinedIcon />}
        title="Parents"
        subtitle="Send directly to parent/guardian contacts on file."
        enabled={!! filter.parents?.enabled}
        onToggle={(v) => setSegment("parents", { enabled: v })}
      >
        <Stack spacing={1}>
          <FormControlLabel
            control={<Switch checked={filter.parents?.of_active_students ?? true}
              onChange={(_, v) => setSegment("parents", { of_active_students: v })} />}
            label="Only parents of active students"
          />
          {channel === "email" ? (
            <FormControlLabel
              control={<Switch checked={filter.parents?.is_email_notification ?? true}
                onChange={(_, v) => setSegment("parents", { is_email_notification: v })} />}
              label="Only parents with email notifications enabled"
            />
          ) : (
            <FormControlLabel
              control={<Switch checked={filter.parents?.is_sms_notification ?? true}
                onChange={(_, v) => setSegment("parents", { is_sms_notification: v })} />}
              label="Only parents with SMS notifications enabled"
            />
          )}
        </Stack>
      </SegmentCard>

      {/* ── Staff ────────────────────────────────────────────────── */}
      <SegmentCard
        icon={<BadgeOutlinedIcon />}
        title="Staff"
        subtitle="Reach teachers and staff members through their staff record."
        enabled={!! filter.staff?.enabled}
        onToggle={(v) => setSegment("staff", { enabled: v })}
      >
        <Stack spacing={1.5}>
          <FormControlLabel
            control={<Switch checked={filter.staff?.active ?? true}
              onChange={(_, v) => setSegment("staff", { active: v })} />}
            label="Active staff only"
          />
          <SearchableSelect
            label="Locations"
            options={locations}
            value={optsFromIds(locations, filter.staff?.location_ids)}
            onChange={(v) => setSegment("staff", { location_ids: v.map((o) => o.id) })}
            placeholder="All locations"
          />
          {/* Position/role filter deliberately omitted — teams.teamPositionId
              is a HR job title, not an authorization role. See F1a audit §10. */}
        </Stack>
      </SegmentCard>

      {/* ── Admins ───────────────────────────────────────────────── */}
      <SegmentCard
        icon={<AdminPanelSettingsOutlinedIcon />}
        title="Admins"
        subtitle="Internal admin users. Email only — admins have no primary phone."
        enabled={!! filter.admins?.enabled}
        onToggle={(v) => setSegment("admins", { enabled: v })}
      >
        <FormControlLabel
          control={<Switch checked={filter.admins?.active ?? true}
            onChange={(_, v) => setSegment("admins", { active: v })} />}
          label="Active admins only"
        />
      </SegmentCard>

      <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <ContentPasteOutlinedIcon sx={{ color: B13.primary }} />
          <Typography sx={{ ...B13TypeScale.h3 }}>Paste contacts</Typography>
        </Stack>
        <TextField multiline minRows={5} fullWidth sx={filterFieldSx}
          placeholder={channel === "email"
            ? "john@example.com\nJane Smith <jane@example.com>\nName,email"
            : "9051234567\nName, +1 (905) 555-9999"}
          value={pasted} onChange={(e) => setPasted(e.target.value)}
        />
        {pasted.trim() && (
          <Typography sx={{ mt: 1, fontSize: 12, color: B13Ink.ink2 }}>
            Parsed {pastedPreview.length} contact{pastedPreview.length === 1 ? "" : "s"}
            {" · "}Duplicates & suppression are resolved server-side before send.
          </Typography>
        )}
      </Box>

      <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <UploadFileOutlinedIcon sx={{ color: B13.primary }} />
          <Typography sx={{ ...B13TypeScale.h3 }}>Upload CSV</Typography>
        </Stack>
        <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
        <Button variant="outlined" onClick={() => fileRef.current?.click()}
          sx={{ textTransform: "none", borderRadius: "10px" }}>Choose CSV file</Button>
        {csvError && <Alert severity="error" sx={{ mt: 2 }}>{csvError}</Alert>}
        {csvContacts.length > 0 && (
          <Typography sx={{ mt: 2, fontSize: 12.5, color: B13Ink.ink2 }}>
            {csvContacts.length} contacts parsed from CSV. Server preview will show
            duplicates + suppression in the Review step.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const StepContent: React.FC<any> = ({
  channel, subject, setSubject, preheader, setPreheader, body, setBody,
  testTo, setTestTo, onTest, testSending,
}) => {
  const sms = channel === "sms" ? smsSegments(body) : null;
  return (
    <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        {channel === "email" && (
          <>
            <TextField fullWidth required label="Subject" value={subject}
              onChange={(e) => setSubject(e.target.value)} sx={filterFieldSx} />
            <TextField fullWidth label="Preheader (optional)" value={preheader}
              onChange={(e) => setPreheader(e.target.value)} sx={filterFieldSx} />
          </>
        )}
        <TextField fullWidth required multiline minRows={channel === "email" ? 12 : 5}
          label={channel === "email" ? "Body (HTML supported)" : "SMS body"}
          value={body} onChange={(e) => setBody(e.target.value)} sx={filterFieldSx} />
        {sms && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`${sms.chars} / ${sms.perSegment}`}
              sx={{ height: 22, fontSize: 11, bgcolor: "#F1F5F9", color: B13Ink.ink2 }} />
            <Chip size="small" label={`${sms.segments} SMS`}
              sx={{ height: 22, fontSize: 11, fontWeight: 700,
                bgcolor: sms.segments > 1 ? "#FEF3C7" : "#DCFCE7",
                color:   sms.segments > 1 ? "#92400E" : "#15803D" }} />
            <Chip size="small" label={sms.encoding === "unicode" ? "Unicode" : "GSM-7"}
              sx={{ height: 22, fontSize: 11, bgcolor: "#F1F5F9" }} />
          </Stack>
        )}
        <Alert severity="info" icon={false}>
          Available variables include <code>{"{{first_name}}"}</code>, <code>{"{{full_name}}"}</code>,
          <code> {"{{company_name}}"}</code>. Marketing sends automatically append the unsubscribe
          footer + link — do not include one manually.
        </Alert>
        <Divider />
        <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700 }}>Send a test</Typography>
        <Stack direction="row" spacing={1}>
          <TextField size="small" fullWidth
            placeholder={channel === "email" ? "test@example.com" : "+1 905 555 0100"}
            value={testTo} onChange={(e) => setTestTo(e.target.value)} sx={filterFieldSx} />
          <Button onClick={onTest} disabled={testSending} variant="outlined"
            sx={{ textTransform: "none", borderRadius: "10px", minWidth: 120 }}>
            {testSending ? "Sending…" : "Send test"}
          </Button>
        </Stack>
        <Typography sx={{ fontSize: 12, color: B13Ink.ink2 }}>
          Test sends never affect campaign statistics.
          {" "}{channel === "email" ? "Email goes via Microsoft 365." : "SMS goes via INTALK."}
        </Typography>
      </Stack>
    </Box>
  );
};

const StepReview: React.FC<any> = ({
  name, channel, classification, purpose, subject, body,
  previewStats, previewSamples, previewLoading, onOpenSamples,
}) => (
  <Stack spacing={2}>
    <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
      <Typography sx={{ ...B13TypeScale.h3, mb: 2 }}>Review</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        <Field label="Name" value={name} />
        <Field label="Channel" value={channel.toUpperCase()} />
        <Field label="Classification" value={classification} />
        <Field label="Purpose" value={purpose} />
        {channel === "email" && <Field label="Subject" value={subject} span />}
      </Box>
    </Box>
    <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <GroupsOutlinedIcon sx={{ color: B13.primary }} />
        <Typography sx={{ ...B13TypeScale.h3 }}>Recipient summary</Typography>
      </Stack>
      {previewLoading ? (
        <Typography sx={{ color: B13Ink.ink2 }}>Computing recipient list…</Typography>
      ) : previewStats ? (
        <>
          <Box sx={{ display: "grid",
                     gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 1.5 }}>
            <Metric label="Total source" value={previewStats.total_source} />
            <Metric label="Valid"        value={previewStats.valid}       tone="#15803D" />
            <Metric label="Duplicates"   value={previewStats.duplicates_removed} />
            <Metric label="Suppressed"   value={previewStats.suppressed}   tone="#B91C1C" />
            <Metric label="Consent unknown" value={previewStats.consent_unknown} tone="#92400E" />
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {previewSamples.length > 0 && (
              <Button size="small" onClick={onOpenSamples} sx={{ textTransform: "none" }}>
                View recipient sample ({previewSamples.length})
              </Button>
            )}
          </Stack>
          {previewStats.valid === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No eligible recipients. Adjust your audience or record consent before sending.
            </Alert>
          )}
        </>
      ) : (
        <Typography sx={{ color: B13Ink.ink2 }}>Not previewed yet.</Typography>
      )}
    </Box>
    <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
      <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, mb: 1 }}>Content preview</Typography>
      <Box sx={{ border: `1px solid ${B13.line}`, borderRadius: 2, p: 2, bgcolor: "#FAFBFC",
                 fontSize: 13.5, whiteSpace: "pre-wrap" }}>
        {body || "(empty)"}
      </Box>
    </Box>
  </Stack>
);

const StepSend: React.FC<any> = ({
  scheduleMode, setScheduleMode, scheduledAt, setScheduledAt, previewStats,
}) => (
  <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
    <Typography sx={{ ...B13TypeScale.h3, mb: 2 }}>Deliver</Typography>
    <Stack spacing={1}>
      <FormControlLabel control={
        <Radio checked={scheduleMode === "now"} onChange={() => setScheduleMode("now")} />
      } label="Send now" />
      <FormControlLabel control={
        <Radio checked={scheduleMode === "later"} onChange={() => setScheduleMode("later")} />
      } label="Schedule for later" />
      {scheduleMode === "later" && (
        <TextField type="datetime-local" fullWidth label="Scheduled at"
          value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, mt: 1 }} />
      )}
      {previewStats && previewStats.valid === 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Zero eligible recipients. Send is disabled.
        </Alert>
      )}
      {previewStats && previewStats.valid > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {previewStats.valid} recipient{previewStats.valid === 1 ? "" : "s"} will be contacted.
        </Alert>
      )}
    </Stack>
  </Box>
);

// ═════════ Small presentational helpers ═════════════════════════════

const Field: React.FC<{ label: string; value: string; span?: boolean }> = ({ label, value, span }) => (
  <Box sx={{ gridColumn: span ? "1 / -1" : undefined }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3,
                      textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, color: B13.ink, mt: 0.5, textTransform: "capitalize" }}>{value || "—"}</Typography>
  </Box>
);

const Metric: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone }) => (
  <Box sx={{ border: `1px solid ${B13.line}`, borderRadius: 2, p: 1.25 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3,
                      textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: tone ?? B13.ink,
                      mt: 0.5, letterSpacing: "-0.02em" }}>{value}</Typography>
  </Box>
);

// ── Segment card + searchable multi-select primitives ───────────────
// Kept in this file rather than /Components to avoid over-hoisting on
// the first module that needs them (memory:
// [[feedback_hoist_shared_primitive_on_second_module]] — hoist only
// when a second wizard needs the same shape).

const SegmentCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children?: React.ReactNode;
}> = ({ icon, title, subtitle, enabled, onToggle, children }) => (
  <Box sx={{
    ...surfaceSx, borderRadius: 3, p: 2.5,
    border: `2px solid ${enabled ? B13.primary : B13.line}`,
    bgcolor: enabled ? tint12(B13.primary) : "#fff",
    transition: "border-color 120ms ease, background-color 120ms ease",
  }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{
        width: 40, height: 40, display: "flex", alignItems: "center",
        justifyContent: "center", borderRadius: 2,
        bgcolor: enabled ? B13.primary : "#F1F5F9",
        color:   enabled ? "#fff"       : B13Ink.ink2,
      }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: B13.ink }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2 }}>{subtitle}</Typography>
        )}
      </Box>
      <Switch checked={enabled} onChange={(_, v) => onToggle(v)} />
    </Stack>
    {enabled && (
      <Box sx={{ mt: 2, pl: 0.5 }}>{children}</Box>
    )}
  </Box>
);

const SearchableSelect: React.FC<{
  label: string;
  options: LookupOption[];
  value: LookupOption[];
  onChange: (v: LookupOption[]) => void;
  placeholder?: string;
}> = ({ label, options, value, onChange, placeholder }) => (
  <Autocomplete
    multiple
    size="small"
    disableCloseOnSelect
    options={options}
    value={value}
    onChange={(_, v) => onChange(v as LookupOption[])}
    isOptionEqualToValue={(a, b) => a.id === b.id}
    getOptionLabel={(o) => (o as LookupOption).label}
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder={value.length ? "" : placeholder}
        sx={filterFieldSx}
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <>
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: B13Ink.ink3, ml: 0.5 }} />
              </InputAdornment>
              {params.InputProps.startAdornment}
            </>
          ),
        }}
      />
    )}
    renderTags={(v, getTagProps) =>
      v.map((option, index) => (
        <Chip variant="outlined" size="small"
          label={(option as LookupOption).label}
          {...getTagProps({ index })} key={(option as LookupOption).id} />
      ))
    }
  />
);

const HeaderCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    fontWeight: 700, fontSize: 11, letterSpacing: 0.5,
    textTransform: "uppercase", color: B13Ink.ink3,
    borderBottom: `1px solid ${B13.line}`, pb: 0.75,
  }}>{children}</Typography>
);

function rangeLabel(page: number, perPage: number, total: number): string {
  if (total === 0) return "Showing 0 of 0";
  const from = (page - 1) * perPage + 1;
  const to   = Math.min(page * perPage, total);
  return `Showing ${from}–${to} of ${total}`;
}

function emptyStats(): any {
  return { total_source: 0, valid: 0, duplicates_removed: 0, suppressed: 0,
           invalid_contact: 0, consent_unknown: 0, consent_expired: 0, by_type: {} };
}
function mergeStats(a: any, b: any): any {
  const out: any = { ...a, by_type: { ...a.by_type } };
  for (const k of Object.keys(b)) {
    if (k === "by_type") {
      out.by_type = { ...out.by_type };
      for (const t of Object.keys(b.by_type)) out.by_type[t] = (out.by_type[t] ?? 0) + b.by_type[t];
    } else out[k] = (a[k] ?? 0) + (b[k] ?? 0);
  }
  return out;
}

export default CampaignWizardPage;
